import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { getToneTokens } from "../../../styles/shared";

/* ===========================================================
 * Page shell — 3 zonas
 * =========================================================== */

export const DetailRoot = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

export const StickyHeaderSlot = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const ThreeZoneGrid = styled.div`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 18px;
  padding: 18px;
  max-width: none;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 14px;
  }
`;

export const ContextSticky = styled.aside`
  position: sticky;
  top: calc(var(--app-header-height, 0px) + 76px);
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100vh - var(--app-header-height, 0px) - 96px);
  overflow: auto;
  padding-right: 4px;

  @media (max-width: 1100px) {
    position: static;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }
`;

export const MainColumn = styled.main`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  /* MainColumn e um <main> aninhado: a regra global "main { max-width;
     margin: 0 auto; padding }" (system.css) vaza pra ca. Em grid item,
     "margin: auto" encolhe o elemento ao conteudo e o centraliza na faixa
     (deixava ~200px vazios de cada lado). Neutraliza pra preencher a coluna. */
  max-width: none;
  margin: 0;
  padding: 0;
`;

/* ===========================================================
 * Header bar (Zona A)
 * =========================================================== */

export const HeaderBarRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  max-width: none;
  width: 100%;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

export const HeaderBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 999px;
  padding: 6px 12px;
  min-height: 32px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const HeaderIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const HeaderName = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  & > span.name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 760px) {
    font-size: 18px;
  }
`;

export const HeaderPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`;

export const HeaderPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
`;

export const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  justify-self: end;

  @media (max-width: 760px) {
    justify-self: start;
  }
`;

export const HeaderDot = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.5;
`;

/* ===========================================================
 * Context rail (Zona B)
 * =========================================================== */

export const RailCard = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const RailTitle = styled.h2`
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.textSecondary};
`;

export const NextActionCard = styled.section`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      border-left-width: 4px;
      color: ${tokens.text};
    `;
  }}
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const NextActionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  opacity: 0.85;
`;

export const NextActionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
`;

export const NextActionBody = styled.div`
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.85;
`;

export const NextActionButton = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
`;

export const KpiCard = styled.div`
  ${({ theme, $tone = "default" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const KpiLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
`;

export const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
`;

export const DateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;

  span:first-child {
    color: ${({ theme }) => theme.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

export const HealthRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HealthMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};

  strong {
    font-size: 14px;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const PedidoNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow: auto;
`;

export const PedidoNavItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.accent : theme.border)};
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : "transparent")};
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.textPrimary};

  &:hover {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surfaceAlt};
  }

  strong {
    font-size: 12px;
    font-weight: 700;
  }

  span {
    font-size: 11px;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

/* ===========================================================
 * Zona C — Pedidos panel
 * =========================================================== */

export const PanelToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  padding: 10px 12px;
`;

export const SearchBox = styled.label`
  position: relative;
  flex: 1 1 280px;
  min-width: 0;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.textSecondary};
  display: inline-flex;
  pointer-events: none;
`;

export const SearchInput = styled.input`
  width: 100%;
  min-height: 36px;
  padding: 7px 12px 7px 32px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
  }
`;

export const StatusChipsRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const StatusChip = styled.button`
  ${({ theme, $tone = "default", $active }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${$active ? tokens.background : "transparent"};
      border: 1px solid ${$active ? tokens.border : theme.border};
      color: ${$active ? tokens.text : theme.textSecondary};
    `;
  }}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 3px 10px;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const ToolbarMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  margin-left: auto;
`;

export const ClearLink = styled.button`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.accent};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;

  &:hover {
    text-decoration: underline;
  }
`;

export const SectionShell = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
`;

export const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  span.chev {
    color: ${({ theme }) => theme.textSecondary};
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
    display: inline-flex;
  }
`;

export const SectionBody = styled.div`
  padding: 8px 14px 14px;
`;

export const PedidoCardEmpty = styled.div`
  padding: 12px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
`;

/* ===========================================================
 * Drawer
 * =========================================================== */

export const DrawerOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  /* Bugfix 2026-05-29: era 90, atras do header global (100). 200
     cobre tudo (UX padrao de drawer/modal). */
  z-index: 200;
  background: rgba(10, 15, 25, 0.34);
  display: flex;
  justify-content: flex-end;
`;

export const DrawerPanel = styled(motion.aside)`
  width: min(560px, 100vw);
  height: 100%;
  background: ${({ theme }) => theme.surface};
  border-left: 1px solid ${({ theme }) => theme.border};
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const DrawerHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.25;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const DrawerSub = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;

  strong {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

export const DrawerClose = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    border-color: ${({ theme }) => theme.accent};
  }
`;

export const DrawerBody = styled.div`
  flex: 1 1 auto;
  overflow: auto;
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
